// preHydration.ts — pure helper used by both the inline pre-hydration script
// and the React ThemeProvider so the two cannot drift.
//
// Plan ref: 06-00-PLAN-theme-tokens-tailwind-themeprovider.md §Task 1 step 8.
//
// Resolution priority (per REVISION 3):
//   1. URL ?theme query param ('light' | 'dark')        — wins (used by lhci)
//   2. localStorage.studio.theme ('light' | 'dark')     — explicit user pref
//   3. localStorage.studio.theme === 'system' OR empty  → prefers-color-scheme
//   4. Hard fallback                                    → 'dark' (dark-first)
//
// The inline script in index.html implements the same algorithm but is
// hand-tuned for byte size; the unit suite drives this helper to verify the
// algorithm and asserts the inline script's branches via regex.

export type ThemePref = 'dark' | 'light' | 'system';
export type Resolved = 'dark' | 'light';

export interface ResolveInput {
  /** Raw value of the ?theme query param (or null if absent). */
  query: string | null;
  /** Raw value of localStorage.studio.theme (or null if absent / unreadable). */
  stored: string | null;
  /** Whether matchMedia('(prefers-color-scheme: light)') currently matches. */
  prefersLight: boolean;
}

const VALID_PREFS = new Set<ThemePref>(['dark', 'light', 'system']);

export function isValidPref(s: unknown): s is ThemePref {
  return typeof s === 'string' && VALID_PREFS.has(s as ThemePref);
}

export function resolvePreHydrationTheme(input: ResolveInput): Resolved {
  if (input.query === 'light' || input.query === 'dark') {
    return input.query;
  }
  if (input.stored === 'light' || input.stored === 'dark') {
    return input.stored;
  }
  // 'system', invalid, or empty → fall through to media query.
  return input.prefersLight ? 'light' : 'dark';
}

/** Read a *user-facing* preference (what the radio button shows) — separate
 *  from `resolvePreHydrationTheme` which always returns a concrete theme. */
export function readStoredPref(stored: string | null): ThemePref {
  if (isValidPref(stored)) return stored;
  return 'system';
}
